import type { ReactElement, ReactNode, CSSProperties } from 'react';
import type { Toast as HotToast, ToastOptions } from 'react-hot-toast';

import { isValidElement, cloneElement, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

import useClassy from '@core/hooks/useClassy';
import classy from '@core/utils/classy';
import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';

import classes from './style.module.scss';

// Default duration for toast notifications
const DEFAULT_DURATION = 8000;

/**
 * In order for showToast's toast.custom() to work, <Toaster /> must exist in the DOM
 * Both packages/react/src/Hub and packages/react/src/Dash base index files should already have this component
 * */
export const showToast = (
  notificationToBeToasted: HotToast['message'],
  /** Note: some ToastOptions will not be applied because we are using toast.custom() */
  options: ToastOptions = {},
) => {
  toast.custom(
    t => {
      // We need to tap into the toast state to properly have enter/exit animations
      const notification =
        typeof notificationToBeToasted === 'function' ? notificationToBeToasted(t) : notificationToBeToasted;

      // Pass in the toast id to the Toast component so that it can take actions on it
      interface HasToastId {
        toastId: string;
      }
      const notificationWithToastId = isValidElement(notification)
        ? cloneElement(notification as ReactElement<HasToastId>, { toastId: t.id })
        : notification;

      return (
        <div
          className={classy(
            classes.ToastContainer,
            t.visible ? classes.ToastContainer_open : classes.ToastContainer_close,
            options.className,
          )}
        >
          {notificationWithToastId}
        </div>
      );
    },
    {
      position: 'bottom-right',
      duration: DEFAULT_DURATION,
      ...options,
    },
  );
};

export interface ToastProps {
  /** Optional ariaProps - defaults to role=’status’ with aria-live=’polite’ */
  ariaProps?: {
    'aria-live': 'assertive' | 'off' | 'polite';
    role: 'alert' | 'status';
  };
  /** Content rendered in the toast; doesn’t work with title or description */
  children?: ReactNode;
  className?: string;
  /** Optional description (will only be used if children prop is not passed) */
  description?: ReactNode;
  /** Whether or not toast can be dismissed via close icon */
  dismissible?: boolean;
  /** Optional emoji to show to the left of toast content */
  emoji?: string;
  /** Optional onClick callback */
  handleClick?: () => void;
  /** Optional override for icon */
  iconName?: string; // todo: this should map to valid <Icon> enum/types
  /** Type of toast (determines default icon and color) */
  kind?: 'destructive' | 'error' | 'success' | 'warning';
  /** Optional set of inline styles. Usually `className` is preferred, but this
   * is useful for setting styles that need to be computed by JS. */
  style?: CSSProperties;
  theme?: 'dark' | 'light';
  /** Optional title (will only be used if children prop is not passed) */
  title?: string;
  /** Passed in when called using showToast(), can be used to take actions on a specific toast instance (e.g. dismissing) */
  toastId?: string;
}

const Toast = ({
  ariaProps = {
    role: 'status',
    'aria-live': 'polite',
  },
  children,
  className,
  description,
  dismissible = false,
  handleClick,
  iconName,
  kind,
  style,
  theme = 'light',
  title,
  toastId,
}: ToastProps) => {
  const bem = useClassy(classes, 'Toast');
  const [isOpen, setIsOpen] = useState(true);

  const handleOnClick = useCallback(() => {
    setIsOpen(!isOpen);
    toast.dismiss(toastId);

    handleClick?.();
  }, [handleClick, isOpen, toastId]);

  const icon = useMemo(() => {
    // If iconName is provided, use that
    if (iconName) return iconName;

    switch (kind) {
      case 'destructive':
        return 'alert-circle';
      case 'error':
        return 'x';
      case 'success':
        return 'check';
      case 'warning':
        return 'alert-triangle';
      default:
        return null;
    }
  }, [iconName, kind]);

  const content = useMemo(() => {
    if (children) return children;

    return (
      <Flex gap="2px" layout="col">
        {!!title && <div className={bem('-title')}>{title}</div>}
        {!!description && <div className={bem('-description')}>{description}</div>}
      </Flex>
    );
  }, [bem, children, description, title]);

  return (
    <div
      className={bem(
        '&',
        'rm-Notification',
        `_${isOpen ? 'open' : 'close'}`,
        theme && `_${theme}`,
        kind && `_${kind}`,
        className,
      )}
      data-color-mode={theme}
      style={style}
      {...ariaProps}
    >
      {!!icon && (
        <div className={bem('-icon')}>
          <Icon name={icon} size={14} />
        </div>
      )}

      {content}

      {!!dismissible && (
        <Button
          aria-label="Dismiss notification"
          className={bem('-dismissBtn')}
          ghost
          kind="minimum"
          onClick={handleOnClick}
          size="sm"
        >
          <Icon name="x" size={14} />
        </Button>
      )}
    </div>
  );
};

export { default as ErrorToast } from './ErrorToast';
export { default as Toaster, BaseToaster } from './Toaster';
export default Toast;
